import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  price: number;
  originalPrice: number | null;
  badge: string;
  rating: number;
  reviewCount: number;
  heroImage: string;
  features: string[];
  category: string;
}

interface ProductDetailHeroProps {
  product: Product;
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  const [qty, setQty] = useState(1);

  return (
    <section className="pt-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-400 mb-8">
          <Link to="/" className="hover:text-[#0B5C4D] cursor-pointer transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/products" className="hover:text-[#0B5C4D] cursor-pointer transition-colors">Products</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-stone-700 font-medium truncate max-w-48">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <div className="relative bg-[#F5EFE6] rounded-3xl overflow-hidden h-[420px] lg:h-[500px]">
            <img
              src={product.heroImage}
              alt={product.name}
              className="w-full h-full object-cover object-top"
            />
            {product.badge && (
              <span className="absolute top-5 left-5 bg-[#FF6B35] text-white text-sm font-bold px-4 py-1.5 rounded-full">
                {product.badge}
              </span>
            )}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center">
                  <i className="ri-sun-fill text-[#FF6B35] text-lg" />
                </div>
                <div>
                  <p className="text-stone-900 font-bold text-sm">Sun-Powered Cure</p>
                  <p className="text-stone-500 text-xs">Hardens in 1–3 min in direct sunlight</p>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div>
            <p className="text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-2 capitalize">{product.category} kit</p>
            <h1 className="text-3xl md:text-4xl font-black text-stone-900 mb-3">{product.name}</h1>
            <p className="text-stone-500 text-base mb-4 leading-relaxed">{product.tagline}</p>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-yellow-400 text-sm" />
                ))}
              </div>
              <span className="text-stone-700 font-semibold text-sm">{product.rating}</span>
              <span className="text-stone-400 text-sm">({product.reviewCount.toLocaleString()} reviews)</span>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl font-black text-stone-900">${product.price}</span>
              {product.originalPrice && (
                <>
                  <span className="text-stone-400 text-xl line-through">${product.originalPrice}</span>
                  <span className="bg-emerald-100 text-emerald-700 text-sm font-bold px-3 py-1 rounded-full">
                    Save ${(product.originalPrice - product.price).toFixed(2)}
                  </span>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 mb-7">
              {product.features.map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <i className="ri-check-line text-[#0B5C4D] text-sm" />
                  <span className="text-stone-600 text-sm">{f}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-stone-600 hover:text-stone-900 cursor-pointer">
                  <i className="ri-subtract-line" />
                </button>
                <span className="w-10 text-center font-bold text-stone-900">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center text-stone-600 hover:text-stone-900 cursor-pointer">
                  <i className="ri-add-line" />
                </button>
              </div>
              <button className="whitespace-nowrap flex-1 bg-[#FF6B35] text-white font-bold py-3.5 rounded-full hover:bg-[#e55a25] transition-colors cursor-pointer">
                Buy Now — ${(product.price * qty).toFixed(2)}
              </button>
            </div>
            <button className="whitespace-nowrap w-full border-2 border-[#0B5C4D] text-[#0B5C4D] font-bold py-3.5 rounded-full hover:bg-[#0B5C4D] hover:text-white transition-all cursor-pointer">
              Add to Cart
            </button>

            <div className="flex gap-5 mt-5">
              {['Free Shipping $50+', 'TSA Approved', 'Easy Returns'].map((t) => (
                <div key={t} className="flex items-center gap-1.5 text-stone-400 text-xs">
                  <i className="ri-check-line text-[#0B5C4D] text-xs" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
